import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { catchError, forkJoin, map, of, Subject, switchMap, timer } from 'rxjs';
import tinycolor from 'tinycolor2';

import { TermsOfServiceDialog } from './components/dialogs/terms-of-service/terms-of-service.dialog';
import { BaseComponent } from './core/abstractions/base.component';
import { AuthGuard } from './core/security/guards/auth.guard';
import { Color } from './models/core/color';
import { AuthService } from './services/auth.service';
import { BasicDataService } from './services/basic-data.service';
import { LocalStorageCleanupService } from './services/local-storage-cleanup.service';
import { AuthResource } from './services/resources/auth.resource';
import { AppSessionStorage } from './services/storage/app.session-storage';
import { SecurityStorage } from './services/storage/security.storage';
import { TitleService } from './services/title.service';
import { TranslationService } from './services/translation.service';
import { UserSessionService } from './services/user-session.service';

@Component({
    selector: 'sl-app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent extends BaseComponent implements OnInit {
    readonly companyName = signal('');
    readonly primaryColor = signal('#00b3b3');
    readonly primaryColorPalette = signal<Color[]>([]);
    readonly secondaryColor = signal('#5f5e5e'); // accent color
    readonly secondaryColorPalette = signal<Color[]>([]);
    readonly warningColor = signal('#e3170a'); // warning color
    readonly warningColorPalette = signal<Color[]>([]);
    readonly grayColor = signal('#5f5e5e'); // base gray color
    readonly grayColorPalette = signal<Color[]>([]);
    readonly isLoading = signal(true);
    readonly isCheckingAuthState = signal(false);

    private loadingTranslationsSub = new Subject<void>();
    private loadingBasicDataSub = new Subject<void>();
    private readonly fallbackTitle;
    private readonly authGuard = inject(AuthGuard);
    private readonly matDialog = inject(MatDialog);
    private readonly translationService = inject(TranslationService);
    private readonly authService = inject(AuthService);
    private readonly authResource = inject(AuthResource);
    private readonly basicDataService = inject(BasicDataService);
    private readonly appSessionStorage = inject(AppSessionStorage);
    private readonly securityStorage = inject(SecurityStorage);
    private readonly userSessionService = inject(UserSessionService);
    private readonly browserTitleService = inject(Title);
    private readonly localStorageCleanupService = inject(LocalStorageCleanupService);
    private readonly titleService = inject(TitleService);

    constructor() {
        super();

        this.localStorageCleanupService.performCleanupIfRequired();

        const currentContext = this.appSessionStorage.getCurrentContext();
        this.fallbackTitle = `${currentContext.charAt(0).toUpperCase()}${currentContext.slice(1)}`;
        const isLoadingSub = forkJoin({
            loadingTranslations: this.loadingTranslationsSub.asObservable(),
            loadingBasicData: this.loadingBasicDataSub.asObservable()
        }).subscribe(() => {
            isLoadingSub.unsubscribe();
            this.isLoading.set(false);
            this.checkTosApproved();
        });
        const authCheckStartSub = this.authGuard.loadingStartedSub.subscribe(() => {
            this.isCheckingAuthState.set(true);
            authCheckStartSub.unsubscribe();
        });
        const authCheckFinishedSub = this.authGuard.loadingFinishedSub.subscribe(() => {
            this.isCheckingAuthState.set(false);
            this.checkTosApproved();
            authCheckFinishedSub.unsubscribe();
        });

        effect(() => {
            const pageTitle = this.titleService.currentPageTitle();
            if (!!pageTitle) {
                this.browserTitleService.setTitle(`${this.companyName()} - ${pageTitle}`);
            } else {
                this.browserTitleService.setTitle(this.fallbackTitle);
            }
        });
    }

    ngOnInit(): void {
        this.authService.isLoggedIn().subscribe(isLoggedIn => {
            this.translationService
                .setup(isLoggedIn)
                .subscribe()
                .add(() => {
                    this.loadingTranslationsSub.next();
                    this.loadingTranslationsSub.complete();
                });

            if (isLoggedIn) {
                this.getBasicData();
                this.setupHealthCheck();
            } else {
                this.loadingBasicDataSub.next();
                this.loadingBasicDataSub.complete();
            }
        });

        this.setColors();
    }

    saveColorPalette(paletteSignal: WritableSignal<Color[]>, hex: string, paletteName: string): void {
        const palette = this.computeColors(hex);
        paletteSignal.set(palette);

        for (const color of palette) {
            const key1 = `--${paletteName}-${color.name}`;
            const value1 = color.hex;
            const key2 = `--${paletteName}-contrast-${color.name}`;
            const value2 = color.darkContrast ? 'rgba(0, 0, 0, 0.87)' : 'white';

            document.documentElement.style.setProperty(key1, value1);
            document.documentElement.style.setProperty(key2, value2);
        }
    }

    saveGrayColor(): void {
        const palette = this.computeGrayscaleColors(this.grayColor());
        this.grayColorPalette.set(palette);

        for (const color of palette) {
            const key1 = `--theme-gray-${color.name}`;
            const value1 = color.hex;

            document.documentElement.style.setProperty(key1, value1);
        }
    }

    private setColors(): void {
        this.saveColorPalette(this.primaryColorPalette, this.primaryColor(), 'theme-primary');
        this.saveColorPalette(this.secondaryColorPalette, this.secondaryColor(), 'theme-secondary');
        this.saveColorPalette(this.warningColorPalette, this.warningColor(), 'theme-warning');
        this.saveGrayColor();
    }

    private computeColors(hex: string): Color[] {
        return [
            this.getColorObject(tinycolor(hex).lighten(52), '50'),
            this.getColorObject(tinycolor(hex).lighten(37), '100'),
            this.getColorObject(tinycolor(hex).lighten(26), '200'),
            this.getColorObject(tinycolor(hex).lighten(12), '300'),
            this.getColorObject(tinycolor(hex).lighten(6), '400'),
            this.getColorObject(tinycolor(hex), '500'),
            this.getColorObject(tinycolor(hex).darken(6), '600'),
            this.getColorObject(tinycolor(hex).darken(12), '700'),
            this.getColorObject(tinycolor(hex).darken(18), '800'),
            this.getColorObject(tinycolor(hex).darken(24), '900'),
            this.getColorObject(tinycolor(hex).lighten(50).saturate(30), 'A100'),
            this.getColorObject(tinycolor(hex).lighten(30).saturate(30), 'A200'),
            this.getColorObject(tinycolor(hex).lighten(10).saturate(15), 'A400'),
            this.getColorObject(tinycolor(hex).lighten(5).saturate(5), 'A700')
        ];
    }

    private computeGrayscaleColors(hex: string): Color[] {
        return [
            this.getGrayColorObject(tinycolor(hex).lighten(90), '50'),
            this.getGrayColorObject(tinycolor(hex).lighten(60), '100'),
            this.getGrayColorObject(tinycolor(hex).lighten(50), '200'),
            this.getGrayColorObject(tinycolor(hex).lighten(25), '300'),
            this.getGrayColorObject(tinycolor(hex).lighten(15), '400'),
            this.getGrayColorObject(tinycolor(hex), '500'),
            this.getGrayColorObject(tinycolor(hex).darken(6), '600'),
            this.getGrayColorObject(tinycolor(hex).darken(12), '700'),
            this.getGrayColorObject(tinycolor(hex).darken(18), '800'),
            this.getGrayColorObject(tinycolor(hex).darken(24), '900')
        ];
    }

    private getColorObject(value: tinycolor.Instance, name: string): Color {
        const c = tinycolor(value);
        return {
            name,
            hex: c.toHexString(),
            darkContrast: c.isLight()
        };
    }

    private getGrayColorObject(value: tinycolor.Instance, name: string): Color {
        const c = tinycolor(value);
        return {
            name,
            hex: c.toHexString(),
            darkContrast: c.isLight()
        };
    }

    private getBasicData(): void {
        this.basicDataService
            .get()
            .subscribe(resp => {
                this.primaryColor.set(resp?.netvotePrimaryColor ?? this.primaryColor());
                this.companyName.set(resp?.companyName ?? this.companyName());
                this.setColors();
            })
            .add(() => {
                this.loadingBasicDataSub.next();
                this.loadingBasicDataSub.complete();
            });
    }

    private setupHealthCheck(): void {
        const healthCheckSub = timer(60000, 60000)
            .pipe(
                switchMap(() => this.authResource.authCheck()),
                map(() => true),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 0) {
                        // if the request failed because there is no internet connection, we don't want to redirect to the login page
                        return of();
                    }

                    // not logged in anymore, go back to login screen
                    this.authService.logout();
                    healthCheckSub.unsubscribe();

                    return of();
                })
            )
            .subscribe();
        this.addSubscriptions(healthCheckSub);
    }

    private checkTosApproved(): void {
        // TODO: this check is temporary, because we don't have the link to the TOS for Non-Netvote-Users yet.
        // TODO: Once we have it, remove this check, so that TOS is also shown for Non-Netvote-Users.
        if (!this.userSessionService.hasNetvoteRole) {
            return;
        }

        const userInfo = this.securityStorage.getUserInfo();

        if (!userInfo || !!userInfo.acceptedTosDate) {
            return;
        } else if (!this.userSessionService.termsDialogRef) {
            this.userSessionService.termsDialogRef = this.matDialog.open(TermsOfServiceDialog, { disableClose: true });
            this.userSessionService.termsDialogRef.afterClosed().subscribe(() => {
                this.userSessionService.termsDialogRef = undefined;
                this.checkTosApproved();
            });
        }
    }
}
